import { FormEvent, useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, ExternalLinkIcon, FacebookIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Contact Pegasus Spas | Brooklyn, Michigan Showroom';
const DESCRIPTION = 'Call, text or contact Pegasus Spas in Brooklyn, Michigan for hot tub information, outdoor-living products, spa service and replacement-cover questions.';
const CANONICAL = 'https://www.pegasus-spas.com/contact.html';
const MAP_SRC = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2960.328431429922!2d-84.24750730000073!3d42.10307639885929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883cdfc2c40f5547%3A0x5c0f2bfd8950d9b8!2sPegasus+Spas+%26+Leisure+Products!5e0!3m2!1sen!2s!4v1438939843207';
const topics = ['Hot Tub Information', 'Outdoor-Living Products', 'Spa Service', 'Replacement Covers', 'Other Information'];
const paths = [{
  title: 'Hot Tub Questions',
  body: 'Compare new, Viking, Sundance and certified pre-owned paths.',
  href: routes.hotTubs,
  linkLabel: 'Explore Hot Tubs'
}, {
  title: 'Spa Service Request',
  body: 'Review service information before contacting us.',
  href: routes.service,
  linkLabel: 'Spa Service Information'
}, {
  title: 'Replacement Cover',
  body: 'Use the guided replacement-cover builder.',
  href: routes.covers,
  linkLabel: 'Order a Replacement Cover'
}, {
  title: 'Shop Supplies Online',
  body: 'Browse spa and pool supplies.',
  href: routes.supplies,
  linkLabel: 'Shop Online'
}];
const fieldClass = 'mt-1.5 block w-full rounded-btn border border-ink/30 bg-white px-3 py-2.5 text-[16px] text-ink transition-colors duration-150 ease-out placeholder:text-ink/45 focus:border-pegasus-blue focus:outline-none focus:ring-2 focus:ring-pegasus-blue/30';
const labelClass = 'block text-[15px] font-semibold text-ink';
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);

  // Design mockup only — the live Website Forge handler will be reconnected at implementation.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };
  return <main data-id="element-999" data-mp="h6j2w10yun69">
      {/* Compact interior hero */}
      <section aria-labelledby="contact-h1" className="w-full bg-water" data-id="element-1000" data-mp="21u1yfl4uyu4p">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1001" data-mp="1wwa46kgqqvxc">
          <div className="max-w-3xl" data-id="element-1002" data-mp="1wu62m96be908">
            <MessageSquareIcon className="h-8 w-8 text-pegasus-blue/70" aria-hidden="true" data-id="element-1003" data-mp="2bl6dekujaezj" />
            <p className="mt-3 text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1004" data-mp="26ljym6z24qom">
              Call, Text or Send a Message
            </p>
            <h1 id="contact-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1005" data-mp="1iwhij3k2n5w8">
              Contact Pegasus Spas
            </h1>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1006" data-mp="1ssc9qn2d29am">
              Have a question about hot tubs, backyard-living products, replacement covers or spa
              service? Reach our team by phone, text or the form below.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1007" data-mp="2z9fw3dwgammg">
              <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1008" data-mp="18tne5ehny9we">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1009" data-mp="1mswt6au4uxys" />
                Call or Text {business.phone}
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1010" data-mp="1rr6pwpvia6mi">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1011" data-mp="25icwt71476jf" />
                Get Directions
              </SiteLink>
            </div>

            <p className="mt-4 text-[16px] leading-6 text-ink/70" data-id="element-1012" data-mp="2fhty7c9c2y9l">
              Visit the showroom in Brooklyn, Michigan.
            </p>
          </div>
        </div>
      </section>

      {/* Quick contact details */}
      <section aria-labelledby="details-heading" className="w-full bg-white" data-id="element-1013" data-mp="2km4t18b1tr4a">
        <h2 id="details-heading" className="sr-only" data-id="element-1014" data-mp="2fnl5j3gt479t">
          Contact details
        </h2>
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1015" data-mp="2fpeb0ffoolg8">
          <dl className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1016" data-mp="2bnsrwcck3axl">
            <div className="border-t border-ink/15 pt-4" data-id="element-1017" data-mp="38bqmrg8nje4w">
              <PhoneIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1018" data-mp="33cnn6bbfy04b" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1019" data-mp="2ymfjia8g53ox">Call or text</dt>
              <dd className="mt-1" data-id="element-1020" data-mp="2uktyg1lrdj3n">
                <a href={business.phoneHref} className="inline-flex min-h-[44px] items-center text-[17px] font-semibold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1021" data-mp="2ujsoy1ke54o1">
                  {business.phone}
                </a>
              </dd>
            </div>

            <div className="border-t border-ink/15 pt-4" data-id="element-1022" data-mp="1pfnc0qhn67a1">
              <MapPinIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1023" data-mp="1anmxtqik7og4" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1024" data-mp="1fn98ptax0slr">Visit</dt>
              <dd className="mt-1" data-id="element-1025" data-mp="11o9jajfkw9oj">
                <address className="not-italic text-[16px] leading-6 text-ink/85" data-id="element-1026" data-mp="11n6vwlhgspdd">
                  {business.street}
                  <br data-id="element-1027" data-mp="16oe2wibs9kiz" />
                  {business.cityStateZip}
                </address>
                <SiteLink to={routes.location} className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1028" data-mp="rrdbkfxi7rl1">
                  Location &amp; Hours
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1029" data-mp="fg48vd3fny8d" />
                </SiteLink>
              </dd>
            </div>

            <div className="border-t border-ink/15 pt-4" data-id="element-1030" data-mp="p5dymmat14tb">
              <ClockIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1031" data-mp="p6ys08lduhpl" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1032" data-mp="kdmh119mm9fb">Store hours</dt>
              <dd className="mt-1 space-y-0.5" data-id="element-1033" data-mp="y510f97g4cmw">
                {business.hours.map(entry => <p key={entry.days} className="text-[16px] leading-6 text-ink/85" data-id="element-1034" data-mp="u3fgx50ep03q">
                    <span className="font-semibold text-ink" data-id="element-1035" data-mp="184rsz5cg4hkc">{entry.days}</span> {entry.time}
                  </p>)}
              </dd>
            </div>

            <div className="border-t border-ink/15 pt-4" data-id="element-1036" data-mp="133c7jc6cw91f">
              <FacebookIcon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1037" data-mp="39wei11cs22e6" />
              <dt className="mt-2.5 text-[15px] font-bold uppercase text-ink/60" data-id="element-1038" data-mp="34xbhnu7qskcj">
                Follow on Facebook
              </dt>
              <dd className="mt-1" data-id="element-1039" data-mp="2j32w8iy5p9jg">
                <a href={routes.facebook} target="_blank" rel="noopener noreferrer" aria-label="Pegasus Spas on Facebook (opens in a new tab)" className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1040" data-mp="2o261a3e4zfrf">
                  Pegasus Spas on Facebook
                  <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1041" data-mp="2a13etpj8c3fz" />
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Form + visit panel */}
      <section aria-labelledby="form-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1042" data-mp="3lhjxdrq2z072">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1043" data-mp="3qir5xt01s3es">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12" data-id="element-1044" data-mp="1esrluiwj41ts">
            {/* Form column */}
            <div className="lg:col-span-7" data-id="element-1045" data-mp="19v9lvi0howxx">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1046" data-mp="15382zd8tg7yx">Send a Message</p>
              <h2 id="form-heading" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1047" data-mp="1056lgwr1ggpk">
                What Can We Help With?
              </h2>
              <p className="mt-3 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-1048" data-mp="r3qjndeahmwy">
                Share a few details and the right person on our team will follow up.
              </p>

              <form onSubmit={handleSubmit} noValidate className="mt-7 max-w-2xl" data-id="element-1049" data-mp="h6c4ri80l66r">
                <p className="text-[15px] text-ink/65" data-id="element-1050" data-mp="2giof0i9icbr6">
                  Fields marked with an asterisk (*) are required.
                </p>

                <div className="mt-5 space-y-5" data-id="element-1051" data-mp="2bl4zzg4zjuy6">
                  <div data-id="element-1052" data-mp="xfeyp63t6947">
                    <label htmlFor="topic" className={labelClass} data-id="element-1053" data-mp="xelvug7z03rc">
                      I'm interested in *
                    </label>
                    <select id="topic" name="topic" required defaultValue="" className={fieldClass} data-id="element-1054" data-mp="sg2aoi17aw01">
                      <option value="" disabled data-id="element-1055" data-mp="16fab1e93oa5t">
                        Select a topic
                      </option>
                      {topics.map(topic => <option key={topic} value={topic} data-id="element-1056" data-mp="epysc25fzmfv">
                          {topic}
                        </option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-1057" data-mp="ni92jjgc7rx4">
                    <div data-id="element-1058" data-mp="ipz8br86w5p1">
                      <label htmlFor="name" className={labelClass} data-id="element-1059" data-mp="ippf7urulif4">
                        Name *
                      </label>
                      <input id="name" name="name" type="text" required autoComplete="name" className={fieldClass} data-id="element-1060" data-mp="3r47sqc0k5q64" />
                    </div>
                    <div data-id="element-1061" data-mp="3htbq78azbvm0">
                      <label htmlFor="telephone" className={labelClass} data-id="element-1062" data-mp="332umtjesavmh">
                        Telephone *
                      </label>
                      <input id="telephone" name="telephone" type="tel" required autoComplete="tel" className={fieldClass} data-id="element-1063" data-mp="382h01sum4bva" />
                    </div>
                  </div>

                  <div data-id="element-1064" data-mp="3lvgk885cau96">
                    <label htmlFor="email" className={labelClass} data-id="element-1065" data-mp="3qrnntua9jtny">
                      Email *
                    </label>
                    <input id="email" name="email" type="email" required autoComplete="email" className={fieldClass} data-id="element-1066" data-mp="2ad5mz9dt91mg" />
                  </div>

                  <div data-id="element-1067" data-mp="gw9f13w930bj">
                    <label htmlFor="address" className={labelClass} data-id="element-1068" data-mp="gvzxm4p9u1hg">
                      Address
                    </label>
                    <input id="address" name="address" type="text" autoComplete="street-address" className={fieldClass} data-id="element-1069" data-mp="75er7v3cvsnz" />
                  </div>

                  <div data-id="element-1070" data-mp="3u8tcs9e4hdfl">
                    <label htmlFor="message" className={labelClass} data-id="element-1071" data-mp="3pb06lgctkjfb">
                      Questions or comments
                    </label>
                    <textarea id="message" name="message" rows={5} className={fieldClass} data-id="element-1072" data-mp="3kcylyqzqwc1q" />
                  </div>
                </div>

                <button type="submit" className="mt-7 inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1073" data-mp="1nhkcg9hiwmfl">
                  Send Message
                </button>
              </form>
            </div>

            {/* Location column */}
            <div className="lg:col-span-5" data-id="element-1074" data-mp="1zw8mtu2xumjn">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1075" data-mp="24t8u28bixvcc">
                Visit the Showroom
              </p>
              <h2 className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[32px] lg:leading-[40px]" data-id="element-1076" data-mp="1771pgyf802zz">
                Find Us in Brooklyn
              </h2>

              <div className="mt-5 aspect-[4/3] w-full overflow-hidden border border-ink/15" data-id="element-1077" data-mp="1h350yuvypdzl">
                <iframe src={MAP_SRC} title="Map showing Pegasus Spas in Brooklyn, Michigan" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-full w-full border-0" data-id="element-1078" data-mp="1m2rfr8rfv2ai" />
              </div>

              <address className="mt-5 not-italic" data-id="element-1079" data-mp="2v41441ql0w09">
                <p className="text-[16.5px] font-semibold leading-6 text-ink" data-id="element-1080" data-mp="2r1e8dpqsiqxs">{business.name}</p>
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-1081" data-mp="2r3qwylr1zzjt">{business.street}</p>
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-1082" data-mp="34z2ut20ia64y">{business.cityStateZip}</p>
              </address>

              <div className="mt-4 space-y-0.5" data-id="element-1083" data-mp="3e1u9daoohi0t">
                {business.hours.map(entry => <p key={entry.days} className="text-[16px] leading-6 text-ink/85" data-id="element-1084" data-mp="3a08mcwifjncw">
                    <span className="font-semibold text-ink" data-id="element-1085" data-mp="39yfreeh6irkq">{entry.days}</span> {entry.time}
                  </p>)}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1086" data-mp="3itlqkuly2bea">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1087" data-mp="1k73vktikqzf">
                  <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1088" data-mp="k8hcqy13d0b9" />
                  Get Directions
                </SiteLink>
                <SiteLink to={routes.location} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1089" data-mp="67wtk0r9l2h8">
                  Location &amp; Hours
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1090" data-mp="3joxhuenqnd4y" />
                </SiteLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Choose the right path */}
      <section aria-labelledby="paths-heading" className="w-full bg-water" data-id="element-1091" data-mp="39q89pdln971w">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1092" data-mp="3ep1fejuovtri">
          <h2 id="paths-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1093" data-mp="2vjwlwwvqaebc">
            Need Something Specific?
          </h2>

          <ul className="mt-7 max-w-4xl border-t border-ink/20" data-id="element-1094" data-mp="2lu3k2nle1dka">
            {paths.map(path => <li key={path.title} className="flex flex-col gap-1 border-b border-ink/15 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-1095" data-mp="2gvik8pa1svwd">
                <div data-id="element-1096" data-mp="39k7crzx1fswi">
                  <h3 className="text-[17.5px] font-semibold leading-7 text-ink" data-id="element-1097" data-mp="39kz7z9mq3om9">{path.title}</h3>
                  <p className="mt-0.5 text-[16px] leading-6 text-ink/75" data-id="element-1098" data-mp="34jtdb69i5ra3">{path.body}</p>
                </div>
                <SiteLink to={path.href} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1099" data-mp="2zkyjzadwskbq">
                  {path.linkLabel}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1100" data-mp="8o6kgm5kgoeb" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Social */}
      <section aria-labelledby="social-heading" className="w-full bg-white" data-id="element-1101" data-mp="if189uygnw1v">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1102" data-mp="nbhycup80khl">
          <div className="max-w-3xl" data-id="element-1103" data-mp="rdllwhp5zfj8">
            <h2 id="social-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1104" data-mp="rcjxlbj50wjk">
              Keep Up With Pegasus
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1105" data-mp="11bsplyug013a">
              Follow us on Facebook for store updates, seasonal reminders and
              what is happening locally.
            </p>

            <a href={routes.facebook} target="_blank" rel="noopener noreferrer" aria-label="Follow Pegasus Spas on Facebook (opens in a new tab)" className="mt-6 inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1106" data-mp="333xppxm3i4p3">
              <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-1107" data-mp="pwfxwd7fm8of" />
              Follow Pegasus Spas on Facebook
            </a>
          </div>
        </div>
      </section>

      {/* Final navy CTA */}
      <section aria-labelledby="talk-heading" className="w-full bg-ink" data-id="element-1108" data-mp="zvf8i1brcee2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1109" data-mp="uw2capyzxuyt">
          <div className="max-w-3xl" data-id="element-1110" data-mp="uy5cg2tv54h9">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-1111" data-mp="2b9sqipjtxzm6">Prefer to Talk?</p>
            <h2 id="talk-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1112" data-mp="26bhfibhstj2d">
              Call or Text the Showroom
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1113" data-mp="2iucw9xjgc3sb">
              For a direct conversation, reach us at {business.phone} during regular store
              hours.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1114" data-mp="28wowuuawi83t">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1115" data-mp="2wlhyn7e7fa6c">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1116" data-mp="2rui2438zyu4d" />
              Call {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1117" data-mp="3fjkg914fa4u4">
              Plan Your Visit
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}